<script lang="ts">
    import {getLogger} from 'src/core';
    import {KpPopoverPanel} from 'shared/ui-widgets/popover';
    import type {FormControlOptions} from '../../forms/types';
    import type {TextValueEditorValidations} from '../text/types';
    import type {GenerableTextValueEditorLocalizations, GenerableTextValueEditorOptions} from './types';
    import type {EditorsLocalizationFunction} from '../../../localizations/types';
    import type {FormControl} from '../../forms/form-control';
    import type {ValueEditorSize} from '../../../types';
    import {FormControlBuilder} from '../../forms/form-control';
    import {formControls} from '../../forms/use.form-controls';
    import {style} from 'svelte-forms';
    import {getContext, onDestroy} from 'svelte';
    import {focusOnMount} from '../../shared/use.focus-on-mount';
    import {touched} from '../../shared/use.touched';
    import {ariaInvalid} from '../../errors/use.aria-invalid';
    import {ariaErrormessage} from '../../errors/use.aria-errormessage';
    import {validatorFactory} from '../text/utils';
    import {distinctUntilKeyChanged} from 'rxjs/operators';
    import {emptyAsNullFormatter, emptyAsNullParser} from '../_shared/editors-utils';
    import {createInputDescription} from '../../description/utils';
    import {ariaDescribedby} from '../../description/use.aria-describedby';
    import {required} from '../../forms/use.required';
    import {createFloatingActions} from 'svelte-floating-ui';
    import {floatingUiCommonSettings} from 'shared/utils/floating-ui-common-settings';
    import {isString} from 'shared/utils/string-utils';
    import {EDITORS_LOCALIZE} from '../../../context-keys';
    import ErrorMessages from '../../errors/ErrorMessages.svelte';
    import Description from '../../description/Description.svelte';
    import IconedContent from 'shared/ui-widgets/uicons/IconedContent.svelte';

    export let model: string;
    export let formControlOptions: FormControlOptions;

    export let editorId: string;
    export let editorName: string;
    export let placeholder: string;

    export let isDisabled: boolean;
    export let isFocused: boolean;
    export let forceShowErrors: boolean;
    export let size: ValueEditorSize;

    export let options: GenerableTextValueEditorOptions;
    export let validations: TextValueEditorValidations;

    const logger = getLogger();
    const localize: EditorsLocalizationFunction<GenerableTextValueEditorLocalizations> = getContext(EDITORS_LOCALIZE);

    const inputDescription = createInputDescription({editorId, editorName, description: localize('description')});

    const [floatingRef, floatingContent] = createFloatingActions(floatingUiCommonSettings('top'));

    let openPopover: boolean;
    let popoverError: unknown;

    let inputRef: HTMLInputElement;

    const formControl = FormControlBuilder.for(editorId, editorName, model)
        .withValidators(validatorFactory(validations, 'text'))
        .withOptions(formControlOptions)
        .withModifiers([emptyAsNullParser(options)])
        .withFormatters([emptyAsNullFormatter(options)])
        .build(logger);

    const subscription = formControl.getModelValue$().subscribe((value) => model = value);  // two-way data binding -> sending updated value up to a parent
    $: formControl.setModelValue(model); // two-way data binding -> receiving new model value from parent

    const subscriptionForHidingPopover = formControl.getFieldState$().pipe(distinctUntilKeyChanged('value')).subscribe(() => openPopover = false);

    let generationButton: HTMLButtonElement;

    onDestroy(() => {
        subscription.unsubscribe();
        subscriptionForHidingPopover.unsubscribe();
    });

    export function getFormController(): FormControl<string> {
        return formControl;
    }

    async function generate() {
        if (typeof options.requestFunction === 'function') {
            const originalButtonCursor = generationButton.style.cursor;
            generationButton.disabled = true;
            generationButton.style.cursor = 'wait';
            openPopover = false;

            let value: string;

            try {
                value = await options.requestFunction({
                    requestParameters: options.requestParameters,
                    additionalParameters: {
                        inputName: editorName,
                        currentValue: model
                    }
                });
            } catch (e) {
                openPopover = true;
                popoverError = e;
                logger.error(e);
            } finally {
                generationButton.disabled = false;
                generationButton.style.cursor = originalButtonCursor;
                inputRef.focus(); // this also set touched on form control
            }
            if (value) {
                if (isString(value)) {
                    model = value;
                } else {
                    throw new TypeError(`Type of response value must be string: ${value}`);
                }
            }
        } else {
            throw new TypeError(`requestFunction option is not Function: ${options.requestFunction}`);
        }
    }
</script>

<KpPopoverPanel use={[floatingContent]} bind:open={openPopover} additionalClasses="generation-error-popover">
    <div role="status" aria-live="assertive" aria-label={localize('generationError')}>
        <IconedContent icon="exclamation" iconColor="var(--danger-red)">
            {popoverError}
        </IconedContent>
    </div>
</KpPopoverPanel>

<div class="input-group input-group-{size}">
    <div class="input-group-btn">
        <button bind:this={generationButton} class="btn btn-default generate input-{size}" type="button" disabled={isDisabled} on:click={generate} use:floatingRef>
            {localize('generate')}
        </button>
    </div>

    <input id={editorId}
           class="form-control input-{size}"
           name={editorName}
           type="text"
           disabled={isDisabled}
           use:required={validations.required}
           maxlength={validations.maxlength}
           minlength={validations.minlength}
           {placeholder}
           use:ariaErrormessage={formControl}
           use:ariaInvalid={formControl}
           use:focusOnMount={isFocused}
           use:formControls={formControl}
           use:style={{field: formControl.getFieldStateAsStore()}}
           use:touched={{formControl}}
           use:ariaDescribedby={inputDescription}

           bind:this={inputRef}
           data-main-input/>
</div>

<ErrorMessages formController={formControl} {forceShowErrors} {size}/>
<Description {inputDescription}/>